import {useStore} from '@/store/root-store';
import {Modal} from '@/components/common-components/';
import {observer} from 'mobx-react-lite';
import {Dispatch, SetStateAction, useEffect, useState} from 'react';
import {SingleCluster} from './SingleCluster';
import styles from './style.module.scss';
import {Collapse, Divider} from 'antd';
import {toJS} from 'mobx';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTrash} from '@fortawesome/pro-light-svg-icons';
import styled from 'styled-components';
import {formatLargeNumber} from '@/utils/number';
import {faXmark} from '@fortawesome/pro-regular-svg-icons';
import {GrayButton} from '@/components/common-components/v2';

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction <boolean>>;
}

export const BulkRemoveTermsModal = observer(({
  isModalOpen,
  setIsModalOpen,
}: Props) => {
  const {
    contentOptimizer: {
      currentPage,
    },
  } = useStore('');

  const [clusteredTerms, setClusteredTerms] = useState([]);
  const [clusterToRemove, setClusterToRemove] = useState<string>('');

  const setTerms = () => {
    const clusterTerms = toJS(currentPage?.analytics?.clusters);
    const tempArr = [];

    setClusteredTerms(toJS(clusterTerms));

    if (currentPage.focusTermsSorter.key) {
      clusterTerms?.forEach((item, id) => {
        let tempCluster = [];

        tempCluster = item?.focusTerms?.sort((a, b) => {
          if (b[currentPage.focusTermsSorter.key] < a[currentPage.focusTermsSorter.key]) return -1;
        });

        tempArr.push({
          ...clusterTerms[id],
          focusTerms: tempCluster,
        });
      });


      setClusteredTerms(tempArr);
    }
  };

  useEffect(() => {
    setTerms();
  }, [...(currentPage?.analytics?.focusTerms ?? [])]);


  const removeClusterHandler = async (e, clusterTitle: string) => {
    e.preventDefault();
    e.stopPropagation();
    const clusterToRemove = clusteredTerms?.find(cluster => cluster.title === clusterTitle);
    const termsArr = clusterToRemove?.focusTerms?.map(term => term.name);
    setClusterToRemove(clusterTitle);
    try {
      await currentPage.bulkRemoveTerms(termsArr, 'cluster', clusterTitle);
    } catch (e) {
      setClusterToRemove('');
    } finally {
      setClusterToRemove('');
    }
  };

  return <Modal
    visible={isModalOpen}
    width={630}
    style={{borderRadius: '8px'}}
    // centered
    destroyOnClose={true}
    closeIcon={<FontAwesomeIcon icon={faXmark} />}
    className={styles.modal}
    onClose={() => setIsModalOpen(false)}
    closable={true}
    footer={null}>
    <>
      <ModalTitle>Bulk Remove Topical Terms</ModalTitle>
      <CollapseStyled expandIconPosition={'right'}>
        {clusteredTerms?.filter(cluster => cluster.focusTerms?.length > 0)?.map((cluster, idx) => {
          return <Collapse.Panel
            key={idx}
            header={<ClusterHeader>
              <ClusterName>{cluster.title}</ClusterName>
              <ClusterDesc>{`(Terms: ${cluster.focusTerms?.length}, MSV: ${formatLargeNumber(cluster.clusterSv)})`}</ClusterDesc>
            </ClusterHeader>}
            extra={<RemoveClusterBtn disabled={currentPage.isBulkRemovingCluster || clusterToRemove == cluster.title} onClick={e => removeClusterHandler(e, cluster.title)}>
              <FontAwesomeIcon icon={faTrash} className='remove-icon'/>
              <span style={{marginTop: 2}}>Remove Cluster</span>
            </RemoveClusterBtn>}>
            <SingleCluster cluster={cluster} isClusterRemoving={clusterToRemove == cluster.title}/>
          </Collapse.Panel>;
        })}
      </CollapseStyled>
      <Divider />
      <div style={{display: 'flex'}}>
        <GrayButton style={{marginLeft: 'auto'}} onClick={() => setIsModalOpen(false)}>Close</GrayButton>
      </div>
    </>


  </Modal>;
});


const RemoveClusterBtn = styled.div<{disabled: boolean}>`
  font-size: 12px;
  color: ${p => p.disabled ? '#A3A4A4': '#4E5156'};
  cursor: ${p => p.disabled ? 'not-allowed': 'pointer'};

  .remove-icon {
    margin-right: 5px;
  }

  &:hover {
    color: #ff6262;
  }
`;

const ClusterHeader = styled.div`
  display: flex;
  align-items: baseline;
`;
const ClusterName = styled.div`
  font-weight: 500;
  color: #121212;
`;
const ClusterDesc = styled.div`
  font-size: 12px;
  color: #4E5156;
  margin-left: 5px;
`;

const CollapseStyled = styled(Collapse)`
  // background: #fff;
  border: none;
  max-height: calc(75vh - 185px);
  overflow-y: auto;

  .ant-collapse-content {
    border-top: 0px solid transparent;
    background-color: #fff;
  }

  .ant-collapse-item {
    border-radius: 10px !important;
    margin-bottom: 10px;
    border: 1px solid #E8E8E8;
    background-color: #fff;
  }

  .ant-collapse-content-box {
    padding-top: 0px;
  }

  // For Internet Explorer
  scrollbar-face-color: rgba(138,138,138,0.32);
  scrollbar-track-color: rgba(138,138,138,0.12);

  // For Google Chrome
  &::-webkit-scrollbar {
    height: 5px;
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(138,138,138,0.32);
  }

  &::-webkit-scrollbar-track {
    background: rgba(138,138,138,0.12);
  }
`;

const ModalTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  color: #121212;
  margin-bottom: 10px;
`;
